import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import { reportToMaxBounty } from '../utils/affiliates';
import { fetchLocalStorageParams, pardotFormSubmission, submitAllDataToSlowForm } from '../utils/browserUtils';
import { AppConstants, FORM_HOST, regularExpressions } from '../utils/constants';
import { ApplyPageForm } from '../utils/formNames';
import { identifyAnalytics, trackFormError, trackFormSubmission } from '../utils/monitoring';
import { getSegmentId } from '../utils/segment';
import { storeData, storeFormData, getFormData } from '../utils/sessionStorage';
import ribbonCheck from '../images/ribbon-check.svg';
import ribbonPlus from '../images/ribbon-plus.svg';
import ButtonLoader from '../components/ButtonLoader';
import EnrollmentFormInput from '../components/FormInputs/FormInput';
import EnrollmentFormBinaryRadioInput from '../components/FormInputs/FormBinaryRadioInput';
import EnrollmentFormError from '../components/FormInputs/FormError';
import styles from '../styles/ApplyPage.module.scss';

const cx = classNames.bind(styles);

const pardotFormUrl = `${FORM_HOST}/l/772833/2020-10-08/961vhz`;
const callbackLocations = {
  success_location: `${FORM_HOST}/l/772833/2020-10-08/961vlx/772833/1602190559qQn8s9ES/apply_page_success.js`,
  error_location: `${FORM_HOST}/l/772833/2020-10-08/961vlz/772833/1602190570VyEjNWCS/apply_page_error.js`,
};

const { emailRegex } = regularExpressions;

// Field Names
const firstNameField = 'first_name';
const lastNameField = 'last_name';
const emailField = 'email';
const authorizedUSAField = 'authorized_usa';

const ApplyPage = ({ maxBounty, setUserEmail }) => {
  const previousData = getFormData(ApplyPageForm);

  const { register, handleSubmit, triggerValidation, errors } = useForm({
    mode: 'onBlur',
    submitFocusError: true,
    defaultValues: previousData,
  });

  const history = useHistory();

  const localStorageParams = fetchLocalStorageParams();

  useEffect(() => {
    // Necessary because react-hook-form has a bug
    // regarding default values on required radio inputs
    if (previousData[authorizedUSAField]) {
      triggerValidation();
    }
    // Remove TrustPilot from keyboard tab navigation
    window.setTimeout(() => {
      const iframe = document.getElementsByTagName('iframe')[0];
      if (iframe) {
        iframe.setAttribute('tabindex', '-1');
      }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [submitInProgress, setSubmitInProgress] = useState(false);

  const { userEmail } = AppConstants;
  const storeUserEmail = (email) => {
    setUserEmail(email);
    storeData(userEmail, email);
  };

  const onSubmit = async (data) => {
    if (submitInProgress) {
      return;
    }
    data.segment_id = await getSegmentId();
    identifyAnalytics({
      email: data[emailField],
      name: `${data[firstNameField]} ${data[lastNameField]}`,
    });
    storeUserEmail(data[emailField]);
    setSubmitInProgress(true);
    storeFormData(ApplyPageForm, data);
    trackFormSubmission({
      data,
      pageName: ApplyPageForm,
      pageNumber: 0,
      message: 'Enrollment Portal Page Submitted',
    });
    pardotFormSubmission(ApplyPageForm, pardotFormUrl, callbackLocations, data, (err, res) => {
      if (err || !res || !res.success) {
        trackFormError({
          err,
          res,
          formName: ApplyPageForm,
        });
        // Retrieves all cached data and performs a slow submit
        // page param is '0'
        submitAllDataToSlowForm(0, ApplyPageForm);
      }
    });
    if (maxBounty) {
      const { sub2 } = maxBounty;
      reportToMaxBounty(data.segment_id, sub2);
    }
    history.push('/1', { from: window.location.href });
  };

  const mustHaveAuthorizedUSA = (authorizedUSA) => {
    return authorizedUSA === 'true';
  };

  return (
    <main className={styles.ApplyPage}>
      <section className={styles.detailBanner}>
        <article>
          <h2>Get Started</h2>
          <h1>Apply for Pathrise today</h1>
          <p className={styles.bannerText}>
            Fast track your way to a top tech company with Pathrise. Our application process is fast, minimal and
            transparent, so you can get started on the next step in your career as soon as possible.
          </p>
        </article>
        <article>
          <div
            className={cx('trustpilot-widget', 'widgetContainer')}
            data-locale="en-US"
            data-template-id="539ad0ffdec7e10e686debd7"
            data-businessunit-id="5b99898fc2a1170001c69cd4"
            data-style-height="350px"
            data-style-width="100%"
            data-theme="light"
            data-tags="SelectedReview"
            data-review-languages="en"
            data-no-reviews="hide"
            data-scroll-to-list="true"
            data-allow-robots="true"
            data-schema-type="Organization"
          >
            <a href="https://www.trustpilot.com/review/pathrise.com" target="_blank" rel="noopener noreferrer">
              Trustpilot
            </a>
          </div>
        </article>
      </section>
      <section className={styles.callToAction}>
        <form method="GET" action={pardotFormUrl} onSubmit={handleSubmit(onSubmit)} noValidate>
          <input type="hidden" name="utm_campaign" value={localStorageParams.utm_campaign} ref={register} />
          <input type="hidden" name="utm_medium" value={localStorageParams.utm_medium} ref={register} />
          <input type="hidden" name="utm_source" value={localStorageParams.utm_source} ref={register} />
          <input type="hidden" name="referrer_url" value={localStorageParams.referrer_url} ref={register} />
          <input type="hidden" name="utm_content" value={localStorageParams.utm_content} ref={register} />
          <input type="hidden" name="first_url_visited" value={localStorageParams.first_url_visited} ref={register} />
          <input type="hidden" name="utm_term" value={localStorageParams.utm_term} ref={register} />
          <input type="hidden" id="gclid_field" name="gclid" value={localStorageParams.gclid} ref={register} />
          <EnrollmentFormInput
            name={firstNameField}
            type="text"
            label="First Name"
            placeholder="&nbsp;"
            inputRef={register({ required: true })}
            error={errors.first_name}
            required
          />
          <EnrollmentFormError errorField={errors.first_name} />
          <EnrollmentFormInput
            name={lastNameField}
            type="text"
            label="Last Name"
            placeholder="&nbsp;"
            inputRef={register({ required: true })}
            error={errors.last_name}
            required
          />
          <EnrollmentFormError errorField={errors.last_name} />
          <EnrollmentFormInput
            name={emailField}
            type="email"
            label="Email"
            placeholder="&nbsp;"
            inputRef={register({ required: true, maxLength: 254, pattern: emailRegex })}
            error={errors.email}
            required
          />
          <EnrollmentFormError
            errorField={errors.email}
            errorText={{ pattern: 'Please check that the email you have entered is correct' }}
          />
          <div className={styles.radioInputs}>
            <p>
              Are you located in the USA or Canada? <em>*</em>
            </p>
            <EnrollmentFormBinaryRadioInput
              name={authorizedUSAField}
              id="authorized_usa"
              label="Yes"
              value="true"
              inputRef={register({ required: true })}
            />
            <EnrollmentFormBinaryRadioInput
              name={authorizedUSAField}
              id="not_authorized_usa"
              label="No"
              value="false"
              inputRef={register({ required: true, validate: mustHaveAuthorizedUSA })}
              onClick={() => {
                triggerValidation('authorized_usa');
              }}
            />
          </div>
          <EnrollmentFormError
            errorField={errors.authorized_usa}
            errorText={{
              validate: "Unfortunately, we're currently only able to work with individuals in the US or Canada.",
            }}
            blockError
          />
          <button id="submit-button" type="submit" className={styles.formSubmit}>
            {submitInProgress ? <ButtonLoader /> : 'Apply Now'}
          </button>
        </form>
        <article className={styles.productGuarantee}>
          <header>
            <div className={styles.guaranteeBlock}>
              <img alt="" src={ribbonPlus} />
              <div className={styles.guaranteeText}>
                <span>14-Day</span>
                <span>Free Trial</span>
              </div>
            </div>
            <div className={styles.guaranteeBlock}>
              <img alt="" src={ribbonCheck} />
              <div className={styles.guaranteeText}>
                <span>12-Month</span>
                <span>Guarantee</span>
              </div>
            </div>
          </header>
        </article>
      </section>
      <a href="https://pathrise.com/privacy" className={styles.privacyPolicy}>
        Privacy Policy
      </a>
    </main>
  );
};

ApplyPage.propTypes = {
  setUserEmail: PropTypes.func.isRequired,
  maxBounty: PropTypes.shape({
    sub2: PropTypes.string,
  }),
};

ApplyPage.defaultProps = {
  maxBounty: false,
};

export default ApplyPage;
